import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ChevronUp, Wallet, Clock, ThumbsUp } from 'lucide-react';
import './KPCardLocation.css';

const LocationSection = styled.section`
  padding: 6rem 0 4rem 0;
  background: white;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const LocationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const MapContainer = styled.div`
  position: relative;
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const MapFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const MapInfoBox = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  background: white;
  padding: 1rem;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-size: 0.875rem;
  color: #333;
  max-width: 200px;
`;

const Coordinates = styled.div`
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const Address = styled.div`
  margin-bottom: 0.5rem;
`;

const MapLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const MapLink = styled.a`
  color: #2563eb;
  text-decoration: none;
  font-size: 0.8rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ContentContainer = styled.div`
  padding: 2rem;
  padding-left: 6rem;

  @media (max-width: 768px) {
    padding: 1rem;
    padding-left: 1rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    line-height: 1.3;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const AccentLine = styled(motion.div)`
  width: 60px;
  height: 3px;
  background: #FF0707;
  margin-bottom: 2rem;
`;

const Description = styled(motion.p)`
  font-size: 1.125rem;
  line-height: 1.7;
  color: #64748b;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

const ScrollToTopButton = styled(motion.button)`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 50px;
  height: 50px;
  background: #FF0707;
  border: none;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(255, 7, 7, 0.3);
  transition: all 0.3s ease;
  z-index: 1000;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 7, 7, 0.4);
  }
`;

const BenefitsSection = styled.div`
  margin-top: 4rem;
  padding: 0 2rem;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const BenefitBox = styled(motion.div)`
  background: white;
  padding: 1.5rem;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  text-align: left;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    background: #FF0707;
  }
`;

const BenefitContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    text-align: center;
  }
`;

const BenefitIcon = styled.div`
  width: 50px;
  height: 50px;
  background: transparent;
  border: 2px solid #FF0707;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FF0707;
  transition: all 0.3s ease;
  flex-shrink: 0;

  ${BenefitBox}:hover & {
    border-color: white;
    color: white;
  }

  @media (max-width: 768px) {
    align-self: center;
  }
`;

const BenefitText = styled.div`
  flex: 1;
`;

const BenefitTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
  transition: all 0.3s ease;

  ${BenefitBox}:hover & {
    color: #1e293b;
  }
`;

const BenefitDescription = styled.p`
  color: #64748b;
  line-height: 1.6;
  font-size: 0.95rem;
  transition: all 0.3s ease;

  ${BenefitBox}:hover & {
    color: white;
  }
`;

const KPCardLocation = () => {
  const [showScrollButton, setShowScrollButton] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollButton(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <LocationSection>
      <Container>
        <LocationGrid>
          <MapContainer>
            <MapFrame
              src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=-22.567222222222222,17.082777777777777&zoom=15&maptype=roadmap"
              title="KeyPlot Investments Location"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </MapContainer>

          <ContentContainer>
            <SectionTitle
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              Why Choose<br />KeyPlot?
            </SectionTitle>
            
            <AccentLine
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{ marginLeft: '0.5rem' }}
            />
            
            <Description
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              With over 2000 On-Street and Off-Street parking bays throughout the city of Windhoek and an experienced management team, KeyPlot Investments has invested in state of the art parking management solutions to bring improved customer satisfaction to the general public.
            </Description>
          </ContentContainer>
        </LocationGrid>

        <BenefitsSection>
          <BenefitsGrid
            as={motion.div}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <BenefitBox
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <BenefitContent>
                <BenefitIcon>
                  <Wallet size={24} />
                </BenefitIcon>
                <BenefitText>
                  <BenefitTitle>Save Money</BenefitTitle>
                  <BenefitDescription>
                    Want to park for extended periods, Off-Street Parking offers best value for money.
                  </BenefitDescription>
                </BenefitText>
              </BenefitContent>
            </BenefitBox>

            <BenefitBox
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <BenefitContent>
                <BenefitIcon>
                  <Clock size={24} />
                </BenefitIcon>
                <BenefitText>
                  <BenefitTitle>Save Time</BenefitTitle>
                  <BenefitDescription>
                    Introduction of Parking management services by KeyPlot will improve parking efficiency in Windhoek.
                  </BenefitDescription>
                </BenefitText>
              </BenefitContent>
            </BenefitBox>

            <BenefitBox
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <BenefitContent>
                <BenefitIcon>
                  <ThumbsUp size={24} />
                </BenefitIcon>
                <BenefitText>
                  <BenefitTitle>Stress Free</BenefitTitle>
                  <BenefitDescription>
                    Guarantee your parking spot by paying weekly or monthly off street parking in advance.
                  </BenefitDescription>
                </BenefitText>
              </BenefitContent>
            </BenefitBox>
          </BenefitsGrid>
        </BenefitsSection>
      </Container>

      <ScrollToTopButton
        onClick={scrollToTop}
        initial={{ opacity: 0, y: 20 }}
        animate={{ 
          opacity: showScrollButton ? 1 : 0, 
          y: showScrollButton ? 0 : 20,
          scale: showScrollButton ? 1 : 0.8
        }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        style={{ 
          pointerEvents: showScrollButton ? 'auto' : 'none',
          visibility: showScrollButton ? 'visible' : 'hidden'
        }}
      >
        <ChevronUp size={24} />
      </ScrollToTopButton>
    </LocationSection>
  );
};

export default KPCardLocation; 